import React, { createContext, useContext, ReactNode } from "react";
import { useActivePatrolUI } from "./ActivePatrolUIProvider";
import { useActivePatrolData } from "./ActivePatrolDataProvider";
import { getUpdatedCheckpoints } from "../../utils/getUpdatedCheckpoints";
import { useNotificationProvider } from "hooks";
import { updatePatrol } from "api/canguard/updatePatrol";
import { getPatrolCompletionInfo } from "../../utils/getPatrolCompletionInfo";
import { useScheduleContext } from "../ScheduleProvider";

interface ActivePatrolLogicContextType {
  startPatrol: (patrol: ScheduleAssignment, initiatedViaEdit: boolean) => void;
  endPatrol: () => void;
  savePatrol: (callback: () => void) => void;
  attemptExitPatrol: () => void;
  finalizeExitPatrol: () => void;
}

const ActivePatrolLogicContext = createContext<
  ActivePatrolLogicContextType | undefined
>(undefined);

export const ActivePatrolLogicProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const {
    setShowDrawerSkeleton,
    setShowLoadingBackdrop,
    setShowUnsavedWarningModal,
    setShowIncompleteWarningModal,
    setShowActivePatrolWarningModal,
    setShowDrawer,
  } = useActivePatrolUI();
  const {
    activePatrolDataRef,
    checklists,
    generalNotesInfo,
    fetchAndSetPatrolData,
    setActivePatrolID,
  } = useActivePatrolData();
  const { updatePatrolInSchedule } = useScheduleContext();
  const { notifySuccess, notifyError } = useNotificationProvider();

  const startPatrol = async (
    patrol: ScheduleAssignment,
    initiatedViaEdit: boolean
  ) => {
    if (activePatrolDataRef.current) {
      if (
        activePatrolDataRef.current.patrolInfo.schedule_assignment_id !==
        patrol.schedule_assignment_id
      ) {
        setShowActivePatrolWarningModal(true);
      } else {
        setShowDrawer(true);
      }
    } else {
      try {
        setShowDrawer(true);
        setShowDrawerSkeleton(true);
        await fetchAndSetPatrolData(patrol, initiatedViaEdit ? false : true);
      } catch (error) {
        setShowDrawer(false);
        notifyError("Error fetching patrol data");
        console.error("[ActivePatrolLogicProvider/startPatrol] Error fetching patrol data:", error);
      } finally {
        setShowDrawerSkeleton(false);
      }
    }
  };

  const endPatrol = () => {
    // End the patrol
    const completionInfo = getPatrolCompletionInfo(
      checklists,
      generalNotesInfo
    );
    if (completionInfo.isComplete) {
      savePatrol(finalizeExitPatrol);
    } else {
      setShowIncompleteWarningModal(true);
    }
  };

  const attemptExitPatrol = () => {
    if (checkForUnsavedChanges()) {
      setShowUnsavedWarningModal(true);
    } else {
      finalizeExitPatrol();
    }
  };

  const finalizeExitPatrol = () => {
    setShowDrawer(false);
    if (!activePatrolDataRef.current) {
      console.error("[ActivePatrolLogicProvider/finalizeExitPatrol] Unexpected error: activePatrolDataRef is null");
    } else {
      updatePatrolInSchedule({ ...activePatrolDataRef.current.patrolInfo });
      activePatrolDataRef.current = null;
      setActivePatrolID(null);
    }
  };

  const checkForUnsavedChanges = () => {
    const updatedCheckpoints = getUpdatedCheckpoints(
      checklists,
      activePatrolDataRef.current?.checklists || []
    );
    const areGeneralNotesUpdated =
      activePatrolDataRef.current?.comment !==
        generalNotesInfo.generalNotes.text ||
      activePatrolDataRef.current?.status !== generalNotesInfo.generalStatus;

    return updatedCheckpoints.length > 0 || areGeneralNotesUpdated;
  };

  const savePatrol = async (callback: () => void = () => {}) => {
    const activePatrolData = activePatrolDataRef.current;
    if (!activePatrolData) return;
    const updatedCheckpoints = getUpdatedCheckpoints(
      checklists,
      activePatrolData.checklists || []
    );
    const areGeneralNotesUpdated =
      activePatrolData.comment !== generalNotesInfo.generalNotes.text ||
      activePatrolData.status !== generalNotesInfo.generalStatus;

    if (updatedCheckpoints.length === 0 && !areGeneralNotesUpdated) {
      notifySuccess("All changes are upto date.");
      callback();
      return;
    }

    try {
      setShowLoadingBackdrop(true);
      if (!activePatrolData.patrolInfo.comment_list_id) return;
      const updated_timestamps = await updatePatrol(
        activePatrolData.patrolInfo.comment_list_id,
        updatedCheckpoints,
        generalNotesInfo.generalNotes.text,
        generalNotesInfo.generalStatus,
        activePatrolData.updateDurationOnSave
      );
      // Update the active patrol data with the new checklist data
      activePatrolDataRef.current = {
        ...activePatrolData,
        checklists,
        comment: generalNotesInfo.generalNotes.text,
        status: generalNotesInfo.generalStatus,
        patrolInfo: {
          ...activePatrolData.patrolInfo,
          patrol_begin_time: updated_timestamps.startTime,
          patrol_end_time: updated_timestamps.updatedEndTime,
        },
      };

      updatePatrolInSchedule(activePatrolDataRef.current.patrolInfo); // reflect completed state

      callback();
      notifySuccess("Saved");
    } catch (error) {
      console.error("[ActivePatrolLogicProvider/savePatrol] Error saving checklists:", error);
      notifyError("Error saving checklists");
    } finally {
      setShowLoadingBackdrop(false);
    }
  };

  return (
    <ActivePatrolLogicContext.Provider
      value={{
        startPatrol,
        endPatrol,
        savePatrol,
        attemptExitPatrol,
        finalizeExitPatrol,
      }}
    >
      {children}
    </ActivePatrolLogicContext.Provider>
  );
};

export const useActivePatrolLogic = () => {
  const context = useContext(ActivePatrolLogicContext);
  if (!context)
    throw new Error(
      "useLogic must be used within an ActivePatrolLogicProvider"
    );
  return context;
};
